import * as THREE from 'three'
// import * as TWEEN from '@tweenjs/tween.js'
import { tweenGroup } from '@js/lib/tween'

import { setupPostProcessor } from '../utils'
import type { RendererOptions } from '../types'
import type { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'



/**
 * Setup WebGL renderer with post-processing
 */
const setupRenderer = (
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  scene: THREE.Scene,
  options: RendererOptions = {}
): THREE.WebGLRenderer | EffectComposer => {
  const hasComposer = options.composer ?? false

  const updateRenderSize = (
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer | EffectComposer
  ): void => {
    // Handle both WebGLRenderer and EffectComposer
    const actualRenderer = (renderer as any).renderer || renderer
    const w = window.innerWidth
    const h = window.innerHeight
    
    actualRenderer.setSize(w, h)
    
    if (!camera) {
      console.error("camera not loaded in scene")
      return
    }
    
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.aspect = w / h
      camera.updateProjectionMatrix()
    }
  }

  let renderer: THREE.WebGLRenderer | EffectComposer = new THREE.WebGLRenderer({
    powerPreference: 'high-performance',
    alpha: true,
    antialias: hasComposer ? false : true,
    canvas,
  })

  THREE.ColorManagement.enabled = true
  renderer.setSize(window.innerWidth, window.innerHeight)
  
  const webglRenderer = renderer as THREE.WebGLRenderer
  webglRenderer.shadowMap.enabled = true
  webglRenderer.shadowMap.type = THREE.PCFShadowMap

  // Post processor replaces the renderer
  if (hasComposer) {
    renderer = setupPostProcessor(webglRenderer, camera, scene, options)
  }

  // Auto resize window
  updateRenderSize(camera, renderer)
  window.addEventListener('resize', () => {
    updateRenderSize(camera, renderer)
  })

  return renderer
}


/* ___________________________________________________________________________
|                                E X P O R T                                  |
|___________________________________________________________________________ */


export abstract class BaseScene {
  protected scene: THREE.Scene
  protected camera: THREE.PerspectiveCamera
  protected renderer: THREE.WebGLRenderer | EffectComposer | undefined = undefined
  protected timer: THREE.Timer
  protected canvas: HTMLCanvasElement
  rendererOptions: RendererOptions = {}

  constructor(canvasId: string) {
    this.timer = new THREE.Timer()
    this.canvas = this.createCanvas(canvasId, "white")
    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    )
  }

  protected startLoop(): void {
    const tick = () => {
      requestAnimationFrame(tick)

      this.timer.update() // IMPORTANT: must update each frame
      const delta = this.timer.getDelta()

      tweenGroup.update()
      this.update(delta)
      this.render()
    }

    tick()
  }

  protected createCanvas = (id: string, backgroundColor: string): HTMLCanvasElement => {
    backgroundColor = backgroundColor || 'white'
    const canvas = document.createElement('canvas')
    document.body.appendChild(canvas)
    canvas.id = id
    canvas.style.backgroundColor = backgroundColor
    return canvas
  }

  protected setupRenderer(
    canvas: HTMLCanvasElement, 
    camera: THREE.PerspectiveCamera, 
    scene: THREE.Scene, 
    rendererOptions: RendererOptions = {}
  ): THREE.WebGLRenderer | EffectComposer {
    this.rendererOptions = rendererOptions
    this.renderer = setupRenderer(canvas, camera, scene, rendererOptions)
    return this.renderer
  }

  protected render(): void {
    if (this.renderer) {
      const r = this.renderer as any
      if (r.render) r.render(this.scene, this.camera)
    }
  }

  /** Override in subclass to add per-frame logic */
  protected abstract update(delta: number): void

}
